<template>
  <v-container>
    <h2>Video Chat - Raum: {{ roomName }}</h2>
    
    <!-- Join Button wenn noch nicht verbunden -->
    <v-row v-if="!room">
      <v-col cols="12">
        <v-card>
          <v-card-title>Video Chat beitreten</v-card-title>
          <v-card-text>
            <p><strong>Status:</strong> {{ connectionStatus }}</p>
            <p><strong>Token:</strong> {{ token ? 'Verfügbar' : 'Wird geladen...' }}</p>
            
            <v-row class="mt-4">
              <v-col cols="12" sm="6">
                <v-switch 
                  v-model="audioEnabled" 
                  label="Mikrofon aktivieren"
                  color="success"
                ></v-switch>
              </v-col>
              <v-col cols="12" sm="6">
                <v-switch 
                  v-model="videoEnabled" 
                  label="Kamera aktivieren"
                  color="success"
                ></v-switch>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-btn 
              @click="joinRoom" 
              color="primary" 
              size="large"
              :disabled="!token || connectionStatus.includes('Verbinde')"
              :loading="connectionStatus.includes('Verbinde')"
            >
              Video Chat starten
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Video Bereiche -->
    <v-row v-if="room">
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title>Lokales Video</v-card-title>
          <v-card-text>
            <div id="local-video" class="video-container"></div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title>Remote Videos</v-card-title>
          <v-card-text>
            <div id="remote-videos" class="video-container"></div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Steuerung -->
    <v-row v-if="room">
      <v-col cols="12">
        <v-card>
          <v-card-text>
            <p><strong>Status:</strong> {{ connectionStatus }}</p>
            <p><strong>Teilnehmer:</strong> {{ participantCount }}</p>
          </v-card-text>
          <v-card-actions>
            <v-btn @click="toggleAudio" :color="audioEnabled ? 'success' : 'error'">
              <v-icon>{{ audioEnabled ? 'mdi-microphone' : 'mdi-microphone-off' }}</v-icon>
              {{ audioEnabled ? 'Audio An' : 'Audio Aus' }}
            </v-btn>
            <v-btn @click="toggleVideo" :color="videoEnabled ? 'success' : 'error'">
              <v-icon>{{ videoEnabled ? 'mdi-video' : 'mdi-video-off' }}</v-icon>
              {{ videoEnabled ? 'Video An' : 'Video Aus' }}
            </v-btn>
            <v-btn @click="leaveRoom" color="error">
              <v-icon>mdi-phone-hangup</v-icon>
              Raum verlassen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { inject, onMounted, ref, onUnmounted } from "vue";
const Video = require('twilio-video');

const baseCalls = inject('baseCalls');
const token = ref('');
const room = ref(null);
const roomName = 'Raum123';
const connectionStatus = ref('Initialisierung...');
const participantCount = ref(0);
const audioEnabled = ref(true);
const videoEnabled = ref(true);

// Token vom Backend abrufen
async function getToken() {
  try {
    let payload = { action: 'gettoken' };
    const result = await baseCalls.chatGet(payload);
    if (result.data.token) {
      token.value = result.data.token;
      connectionStatus.value = 'Bereit zum Beitreten';
    }
  } catch (error) {
    console.error('Fehler beim Abrufen des Tokens:', error);
    connectionStatus.value = 'Fehler beim Token-Abruf';
  }
}

// Cleanup-Funktion
function cleanup() {
  console.log('Cleanup wird ausgeführt...');
  
  // Alle Video-Elemente entfernen
  const localContainer = document.getElementById('local-video');
  const remoteContainer = document.getElementById('remote-videos');
  
  if (localContainer) {
    localContainer.innerHTML = '';
  }
  if (remoteContainer) {
    remoteContainer.innerHTML = '';
  }
  
  // Room-Referenz zurücksetzen
  room.value = null;
  participantCount.value = 0;
}

// Twilio Room beitreten
async function joinRoom() {
  if (!token.value) return;
  
  try {
    // Erst cleanup machen
    cleanup();
    
    connectionStatus.value = 'Verbinde mit Raum...';
    
    const twilioRoom = await Video.connect(token.value, {
      name: roomName,
      audio: audioEnabled.value,
      video: videoEnabled.value
    });
    
    room.value = twilioRoom;
    connectionStatus.value = 'Verbunden';
    participantCount.value = twilioRoom.participants.size + 1;
    
    console.log('Room verbunden, lokale Tracks:', twilioRoom.localParticipant.tracks.size);
    
    // Lokale Tracks anzeigen
    twilioRoom.localParticipant.tracks.forEach(publication => {
      if (publication.track) {
        console.log('Lokaler Track gefunden:', publication.track.kind);
        const element = publication.track.attach();
        styleVideoElement(element);
        document.getElementById('local-video').appendChild(element);
      }
    });
    
    // Event für neue lokale Tracks
    twilioRoom.localParticipant.on('trackPublished', publication => {
      console.log('Lokaler Track veröffentlicht:', publication.track.kind);
      if (publication.track) {
        const element = publication.track.attach();
        styleVideoElement(element);
        document.getElementById('local-video').appendChild(element);
      }
    });
    
    // Event Listeners für Remote-Teilnehmer
    twilioRoom.on('participantConnected', participant => {
      console.log('Teilnehmer verbunden:', participant.identity);
      participantCount.value = twilioRoom.participants.size + 1;
      
      // Bereits vorhandene Tracks
      participant.tracks.forEach(publication => {
        if (publication.isSubscribed && publication.track) {
          console.log('Remote Track bereits abonniert:', publication.track.kind);
          const element = publication.track.attach();
          styleVideoElement(element);
          document.getElementById('remote-videos').appendChild(element);
        }
      });
      
      // Neue Tracks
      participant.on('trackSubscribed', track => {
        console.log('Remote Track abonniert:', track.kind);
        const element = track.attach();
        styleVideoElement(element);
        document.getElementById('remote-videos').appendChild(element);
      });
      
      // Tracks entfernt
      participant.on('trackUnsubscribed', track => {
        console.log('Remote Track abgemeldet:', track.kind);
        track.detach().forEach(element => element.remove());
      });
    });
    
    twilioRoom.on('participantDisconnected', participant => {
      console.log('Teilnehmer getrennt:', participant.identity);
      participantCount.value = twilioRoom.participants.size + 1;
      participant.tracks.forEach(publication => {
        if (publication.track) {
          publication.track.detach().forEach(element => element.remove());
        }
      });
    });
    
    twilioRoom.on('disconnected', () => {
      console.log('Room getrennt');
      connectionStatus.value = 'Getrennt';
      cleanup();
    });
    
  } catch (error) {
    console.error('Fehler beim Beitreten des Raums:', error);
    connectionStatus.value = 'Verbindungsfehler: ' + error.message;
    cleanup();
  }
}

// Video-Element stylen
function styleVideoElement(element) {
  element.style.width = '100%';
  element.style.height = '100%';
  element.style.objectFit = 'cover';
  element.style.borderRadius = '8px';
}

// Audio ein/ausschalten
function toggleAudio() {
  if (room.value && room.value.localParticipant) {
    room.value.localParticipant.audioTracks.forEach(publication => {
      if (audioEnabled.value) {
        publication.track.disable();
      } else {
        publication.track.enable();
      }
    });
    audioEnabled.value = !audioEnabled.value;
  }
}

// Video ein/ausschalten
function toggleVideo() {
  if (room.value && room.value.localParticipant) {
    room.value.localParticipant.videoTracks.forEach(publication => {
      if (videoEnabled.value) {
        publication.track.disable();
      } else {
        publication.track.enable();
      }
    });
    videoEnabled.value = !videoEnabled.value;
  }
}

// Raum verlassen
function leaveRoom() {
  if (room.value) {
    console.log('Raum wird verlassen...');
    room.value.disconnect();
  }
}

// Component lifecycle
onMounted(async () => {
  await getToken();
});

onUnmounted(() => {
  console.log('Component wird unmounted...');
  if (room.value) {
    room.value.disconnect();
  }
  cleanup();
});
</script>

<style scoped>
.video-container {
  width: 100%;
  height: 300px;
  background: #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.video-container video {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  border-radius: 8px;
}

.video-container audio {
  display: none;
}
</style>
